import type { ClipboardEvent, KeyboardEvent } from "react"

function limitByIntegerDigits(input: string, maxIntegerDigits: number): string {
  let integerDigits = 0
  let seenDecimal = false

  return Array.from(input).reduce((acc, ch) => {
    if (ch === ".") {
      seenDecimal = true
      return acc + ch
    }

    if (/\d/.test(ch)) {
      if (!seenDecimal) {
        if (integerDigits >= maxIntegerDigits) return acc
        integerDigits += 1
        return acc + ch
      }

      return acc + ch
    }

    return acc + ch
  }, "")
}

function limitByTotalDigits(input: string, maxDigits: number): string {
  let result = input

  while (result.replace(/\D/g, "").length > maxDigits && result.length > 0) {
    result = result.slice(0, -1)
  }

  return result
}

export type NumericInputDigitLimit = {
  maxDigits?: number
  maxIntegerDigits?: number
}

export type NumericInputTypingInfo = {
  userTyping: boolean
  input: string
}

export function limitNumericInput(
  input: string,
  maxDigits?: number,
  maxIntegerDigits?: number
): string {
  if (maxIntegerDigits != null) {
    return limitByIntegerDigits(input, maxIntegerDigits)
  }

  if (maxDigits != null) {
    return limitByTotalDigits(input, maxDigits)
  }

  return input
}

function limitWithConfig(input: string, limit: NumericInputDigitLimit): string {
  return limitNumericInput(input, limit.maxDigits, limit.maxIntegerDigits)
}

export function hasNumericDigitLimit(limit: NumericInputDigitLimit): boolean {
  return limit.maxIntegerDigits != null || limit.maxDigits != null
}

export function wouldTruncateNumericInput(
  candidate: string,
  limit: NumericInputDigitLimit
): boolean {
  return limitWithConfig(candidate, limit) !== candidate
}

const DEFAULT_DISPLAY_LOCALE = "en-US"

export function formatLimitedNumberInputDisplay(
  value: number | undefined | null,
  info: NumericInputTypingInfo,
  limit: NumericInputDigitLimit,
  precision?: number
): string {
  if (value === undefined || value === null) {
    return ""
  }
  if (info.userTyping) {
    return limitWithConfig(info.input, limit)
  }
  return new Intl.NumberFormat(DEFAULT_DISPLAY_LOCALE, {
    maximumFractionDigits: precision ?? 2,
    notation: "standard",
    compactDisplay: "short"
  }).format(value)
}

const CURRENCY_STRIP_PATTERN = /\$\s?|(,*)/g

export function parseLimitedCurrencyNumericInput(
  value: string | undefined,
  limit: NumericInputDigitLimit
): number {
  const limited = limitWithConfig(value ?? "", limit)
  const normalized = limited.replace(CURRENCY_STRIP_PATTERN, "")
  // Empty must be a valid number: NaN breaks Ant Design InputNumber and the
  // previous value can stick while the user clears the field.
  if (normalized === "") return 0
  return Number(normalized)
}

const BEFORE_INSERT_TYPES = new Set<string>([
  "insertText",
  "insertCompositionText",
  "insertFromPaste"
])

export function getInputValueAfterInsert(
  input: HTMLInputElement,
  insertion: string
): string {
  const start = input.selectionStart ?? 0
  const end = input.selectionEnd ?? 0
  return `${input.value.slice(0, start)}${insertion}${input.value.slice(end)}`
}

function insertWouldExceedLimit(
  limit: NumericInputDigitLimit,
  input: HTMLInputElement,
  insertion: string
): boolean {
  return wouldTruncateNumericInput(
    getInputValueAfterInsert(input, insertion),
    limit
  )
}

function digitCharFromKeyboardEvent(
  e: KeyboardEvent<HTMLInputElement>
): string | null {
  if (e.key.length === 1 && /[\d.,]/.test(e.key)) {
    return e.key
  }
  if (/^Numpad[0-9]$/.test(e.code)) {
    return e.code.slice(-1)
  }
  if (e.code === "NumpadDecimal") {
    return "."
  }
  return null
}

function isModifierOrComposingKeyboardEvent(
  e: KeyboardEvent<HTMLInputElement>
): boolean {
  return (
    e.ctrlKey ||
    e.metaKey ||
    e.altKey ||
    e.nativeEvent.isComposing ||
    e.key === "Dead"
  )
}

export function shouldPreventDefaultForDigitLimitOnBeforeInput(
  e: {
    nativeEvent: globalThis.InputEvent
    currentTarget: HTMLInputElement
  },
  limit: NumericInputDigitLimit
): boolean {
  if (!hasNumericDigitLimit(limit)) {
    return false
  }
  if (e.nativeEvent.isComposing) {
    return false
  }
  if (!BEFORE_INSERT_TYPES.has(e.nativeEvent.inputType)) {
    return false
  }

  return insertWouldExceedLimit(
    limit,
    e.currentTarget,
    e.nativeEvent.data ?? ""
  )
}

export function shouldPreventDefaultForDigitLimitOnPaste(
  e: ClipboardEvent<HTMLInputElement>,
  limit: NumericInputDigitLimit
): boolean {
  if (!hasNumericDigitLimit(limit)) {
    return false
  }

  return insertWouldExceedLimit(
    limit,
    e.currentTarget,
    e.clipboardData.getData("text")
  )
}

export function shouldPreventDefaultForDigitLimitOnKeyDown(
  e: KeyboardEvent<HTMLInputElement>,
  limit: NumericInputDigitLimit
): boolean {
  if (!hasNumericDigitLimit(limit)) {
    return false
  }
  if (isModifierOrComposingKeyboardEvent(e)) {
    return false
  }

  const ch = digitCharFromKeyboardEvent(e)
  if (ch === null) {
    return false
  }

  return insertWouldExceedLimit(limit, e.currentTarget, ch)
}

 
